import React from "react";
import { Chip, Paper, Typography, Box, Stack } from "@mui/material";
import type {
  RetreatLifecycleStatus,
  RetreatTypeOption,
} from "../../../../types/retreatLifecycle";

export function typeChip(
  type?: string,
  options?: {
    onClick?: () => void;
    isSelected?: boolean;
    clickable?: boolean;
  },
) {
  const map: Record<string, { bg: string; color: string }> = {
    "Established Parish": { bg: "#e8f1fb", color: "#285d8f" },
    "New Parish / First Retreat": { bg: "#fff3d6", color: "#8a6300" },
    "Outreach / New Parish": { bg: "#fff3d6", color: "#8a6300" },
    "Archbishop Outreach Retreat": { bg: "#f0e8fa", color: "#66429a" },
    "Bishop-Sponsored": { bg: "#f0e8fa", color: "#66429a" },
  };
  const palette = map[type || ""] || { bg: "#f3f4f6", color: "#4b5563" };
  const isSelected = Boolean(options?.isSelected);
  const isClickable = Boolean(options?.onClick || options?.clickable);

  return (
    <Chip
      size="small"
      label={type || "—"}
      onClick={options?.onClick}
      sx={{
        bgcolor: isSelected ? palette.color : palette.bg,
        color: isSelected ? "#ffffff" : palette.color,
        fontWeight: 700,
        fontFamily: 'Inter, "Segoe UI", Roboto, Arial, sans-serif',
        fontSize: "0.75rem",
        cursor: isClickable ? "pointer" : "default",
        userSelect: isClickable ? "none" : undefined,
      }}
    />
  );
}

export function statusChip(status?: string) {
  const styleMap: Record<string, { bg: string; color: string }> = {
    Projected: { bg: "#f3f4f6", color: "#4b5563" },
    "Pre-Publication Setup": { bg: "#fff0d9", color: "#9a4d00" },
    "Setup in Progress": { bg: "#fff0d9", color: "#9a4d00" },
    "Ready for Publication": { bg: "#dff4e4", color: "#216b30" },
    "Registration Open": { bg: "#dcecff", color: "#0d5ea6" },
    "Closing Progress": { bg: "#f4e8fb", color: "#6b3d8c" },
    "Post-Retreat Closing": { bg: "#f4e8fb", color: "#6b3d8c" },
    Completed: { bg: "#e5e7eb", color: "#374151" },
    Closed: { bg: "#eceff3", color: "#374151" },
    Deleted: { bg: "#fee2e2", color: "#991b1b" },
  };
  const palette = styleMap[status || ""] || {
    bg: "#f3f4f6",
    color: "#4b5563",
  };
  return (
    <Chip
      size="small"
      label={status || "—"}
      sx={{
        bgcolor: palette.bg,
        color: palette.color,
        fontWeight: 700,
        fontFamily: 'Inter, "Segoe UI", Roboto, Arial, sans-serif',
        fontSize: "0.75rem",
      }}
    />
  );
}

export function setupStatusLabel(row: {
  displayStatus?: string;
  readinessPercent?: number;
}) {
  if (
    row.displayStatus === "Setup in Progress" ||
    row.displayStatus === "Ready for Publication"
  ) {
    return row.displayStatus;
  }
  return (row.readinessPercent || 0) >= 100
    ? "Ready for Publication"
    : "Setup in Progress";
}

export function StatusSummaryCard({
  label,
  value,
  detail,
  color,
  onClick,
}: {
  label: string;
  value: number;
  detail: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        minWidth: 0,
        px: { xs: 1.25, sm: 1.5 },
        py: 0.9,
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderRadius: 2,
        borderLeft: `4px solid ${color}`,
        boxShadow: "none",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": onClick
          ? { boxShadow: "0 4px 14px rgba(15,23,42,.08)", transform: "translateY(-1px)" }
          : undefined,
      }}
    >
      <Typography
        sx={{
          flexShrink: 0,
          color,
          fontSize: "1.35rem",
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Box sx={{ minWidth: 0, display: "flex", alignItems: "baseline", gap: 0.75, overflow: "hidden" }}>
        <Typography
          sx={{
            fontSize: { xs: "0.75rem", sm: "0.85rem" },
            fontWeight: 700,
            fontFamily: 'Inter, "Segoe UI", Roboto, Arial, sans-serif',
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: { xs: "normal", sm: "nowrap" },
            lineHeight: 1.25,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: { xs: "none", md: "block" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {detail}
        </Typography>
      </Box>
    </Paper>
  );
}

export function SectionHeader({
  icon,
  title,
  subtitle,
  bgcolor,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  bgcolor: string;
  action?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor,
        display: "flex",
        gap: 2,
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

export function formatTentativeDates(
  dateFrom?: string | null,
  dateTo?: string | null,
): string {
  if (!dateFrom && !dateTo) return "Tentative";
  const fmt = (d?: string | null) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
  if (dateFrom && dateTo) return `${fmt(dateFrom)} – ${fmt(dateTo)}`;
  return fmt(dateFrom || dateTo);
}

export function formatProjectedDates(
  dateFrom?: string | null,
  dateTo?: string | null,
): string {
  const hasDate = Boolean(
    (dateFrom && String(dateFrom).trim()) || (dateTo && String(dateTo).trim()),
  );
  return hasDate ? "Tentative" : "—";
}

export function formatRetreatDateRange(
  dateFrom?: string | null,
  dateTo?: string | null,
): string {
  if (!dateFrom || !dateTo) return "Tentative";
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "Tentative";
  const monthDay = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" });
  const year = (d: Date) =>
    d.toLocaleDateString("en-US", { year: "numeric", timeZone: "America/New_York" });
  const sameDay =
    from.toLocaleDateString("en-US", { timeZone: "America/New_York" }) ===
    to.toLocaleDateString("en-US", { timeZone: "America/New_York" });
  if (sameDay) {
    return `${monthDay(from)}, ${year(from)}`;
  }
  const sameMonth =
    from.toLocaleDateString("en-US", { month: "numeric", year: "numeric", timeZone: "America/New_York" }) ===
    to.toLocaleDateString("en-US", { month: "numeric", year: "numeric", timeZone: "America/New_York" });
  if (sameMonth) {
    const dayTo = to.toLocaleDateString("en-US", { day: "numeric", timeZone: "America/New_York" });
    return `${monthDay(from)} - ${dayTo}, ${year(from)}`;
  }
  return `${monthDay(from)} - ${monthDay(to)}, ${year(from)}`;
}

export function isUpcomingRetreat(row: { dateFrom?: string | null; dateTo?: string | null }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateFrom = row.dateFrom ? new Date(row.dateFrom) : null;
  const dateTo = row.dateTo ? new Date(row.dateTo) : null;
  if (dateFrom) dateFrom.setHours(0, 0, 0, 0);
  if (dateTo) dateTo.setHours(0, 0, 0, 0);
  return Boolean((dateFrom && dateFrom >= today) || (dateTo && dateTo >= today));
}

export function formatLanguage(language?: string | null): string {
  if (!language) return "—";
  const lower = String(language).toLowerCase();
  if (lower === "english") return "English";
  if (lower === "spanish") return "Spanish";
  return String(language).charAt(0).toUpperCase() + String(language).slice(1);
}
